import {
  find,
  includes,
  isEmpty,
  isFunction,
  replace,
  slice,
  some,
  split,
  startsWith,
  toString,
} from 'es-toolkit/compat';

import Visualization from 'interfaces/visualization';
import { f } from 'utils/formatBrackets/formatBracketsFeature';
import {
  bracketsRegex,
  buildClassifiersString,
  conservativeTitleCase,
  getDictBrackets,
  getFormattedArea,
  getFormattedCellWithStainAndClassifiers,
  getFormattedClassificationModels,
  getFormattedStain,
} from '.';
import { FormatBracketsOptions } from './formatBracketsOptions';

const visualizationWithClassifiers = ['v:5', 'v:8', 'v:10'];
export const visualizationIndexRegex = /<v:(\d+)>/;

export const getVisualizationId = (visualizationParts: string[]): string => {
  const visualizationIdPart = find(visualizationParts, (visualizationPart) => startsWith(visualizationPart, 'v:'));
  return split(visualizationIdPart, ':')[1];
};

export const formatBracketsVisualization = (
  key: string,
  bracketTypesOptions: FormatBracketsOptions,
  mainHeatmapKey?: string
): string => {
  try {
    const visualizationParts = slice(split(key, bracketsRegex), 1, -1);
    const visualizationId = getVisualizationId(visualizationParts);
    let formattedVisualization = '';

    if (v[visualizationId]) {
      formattedVisualization = v[visualizationId](visualizationParts, bracketTypesOptions);
    } else {
      const dictBrackets = getDictBrackets(visualizationParts);
      if (dictBrackets['c']) {
        if (
          mainHeatmapKey &&
          some(visualizationWithClassifiers, (visualization) => mainHeatmapKey.includes(visualization))
        ) {
          formattedVisualization = `${buildClassifiersString(dictBrackets['classifiers'], bracketTypesOptions, {
            withParenthesis: false,
            isMarkerPositivity: includes(mainHeatmapKey, 'v:8'),
          })}`;
        } else {
          formattedVisualization = `${getFormattedCellWithStainAndClassifiers(
            dictBrackets['c'],
            dictBrackets['s'],
            dictBrackets['classifiers'],
            bracketTypesOptions
          )}`;
        }
      } else if (dictBrackets['a']) {
        formattedVisualization = `${getFormattedArea(dictBrackets['a'], bracketTypesOptions.areaTypeOptions)}`;
      }
    }

    if (!isEmpty(formattedVisualization)) {
      return replace(replace(conservativeTitleCase(formattedVisualization), /µM/g, 'µm'), /\bDilation\b/g, 'dilation');
    }

    return key;
  } catch (error) {
    console.error(`Error formatting visualization: ${key}`, error);
    return key;
  }
};

const visualizationPartsToFormattedVisualization = (
  visualizationParts: string[],
  { visualizationTypeOptions }: FormatBracketsOptions
) => {
  const dictBrackets = getDictBrackets(visualizationParts);
  return getFormattedVisualization(dictBrackets['v'], visualizationTypeOptions);
};

const v: Record<string, (visualizationParts: string[], options: FormatBracketsOptions) => string> = {
  '1': visualizationPartsToFormattedVisualization,
  '2': visualizationPartsToFormattedVisualization,
  '3': visualizationPartsToFormattedVisualization,
  '4': visualizationPartsToFormattedVisualization,
  '5': (visualizationParts, bracketTypesOptions) => {
    const dictBrackets = getDictBrackets(visualizationParts);
    const formattedCell =
      dictBrackets['c'] === 'all_cells'
        ? ''
        : getFormattedCellWithStainAndClassifiers(
            dictBrackets['c'],
            dictBrackets['s'],
            dictBrackets['classifiers'],
            bracketTypesOptions
          );

    return `${getFormattedClassificationModels(
      dictBrackets['cm'],
      bracketTypesOptions.classificationModelOptions
    )} ${getFormattedVisualization(dictBrackets['v'], bracketTypesOptions.visualizationTypeOptions)} ${formattedCell}`;
  },
  '6': (visualizationParts, options) => {
    const dictBrackets = getDictBrackets(visualizationParts);
    const displayName = visualizationPartsToFormattedVisualization(visualizationParts, options);
    return dictBrackets['dilation'] ? `Dilated ${displayName} - ${dictBrackets['dilation']} µm` : displayName;
  },
  '7': (visualizationParts, options) => {
    const dictBrackets = getDictBrackets(visualizationParts);

    if (!isFunction(f[dictBrackets['f']])) {
      console.warn('No formatting function found', { visualizationParts, dictBrackets });
    }

    return f[dictBrackets['f']](visualizationParts, options);
  },
  '8': visualizationPartsToFormattedVisualization,
  '9': (visualizationParts, bracketTypesOptions) => {
    const dictBrackets = getDictBrackets(visualizationParts);
    const sourceStain = dictBrackets['source_s']?.['s']
      ? ` (Registered from ${getFormattedStain(dictBrackets['source_s']?.['s'], bracketTypesOptions.stainTypeOptions)})`
      : '';
    return `${getFormattedVisualization(
      dictBrackets['v'],
      bracketTypesOptions.visualizationTypeOptions
    )} ${getFormattedCellWithStainAndClassifiers(
      dictBrackets['c'],
      dictBrackets['s'],
      dictBrackets['classifiers'],
      bracketTypesOptions
    )}${sourceStain}`;
  },
  '10': visualizationPartsToFormattedVisualization,
  '11': visualizationPartsToFormattedVisualization,
  '12': visualizationPartsToFormattedVisualization,
};

export const getFormattedVisualization = (index: string, visualizationTypeOptions: Visualization[]) => {
  const visualization = find(
    visualizationTypeOptions,
    (visualizationTypeOption) => toString(visualizationTypeOption.index) === index
  );

  return visualization?.displayName || replace(index, /[_-]/g, ' ');
};
